/**
 * MyCopro - Event Store (avec Firestore)
 * + Notification automatique lors de la création d'un événement
 * src/stores/eventStore.ts
 */

import { create } from 'zustand';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { logger } from '@/utils/logger';

export type EventType = 'ag' | 'travaux' | 'intervention' | 'reunion' | 'autre';

// Labels des types d'événements
const EVENT_TYPE_LABELS: Record<EventType, string> = {
  ag: 'Assemblée Générale',
  travaux: 'Travaux',
  intervention: 'Intervention',
  reunion: 'Réunion',
  autre: 'Autre'
};

export interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  time?: string;
  endTime?: string;
  type: EventType;
  description?: string;
  location?: string;
  /** 'upcoming' | 'past' — computed from date for display purposes */
  status?: 'upcoming' | 'past';
  restrictedTo?: 'owners' | 'council'; // Restriction d'audience
  createdAt?: string;
  createdBy?: string;
  createdByName?: string;
  residenceId?: string;
}

interface EventState {
  events: CalendarEvent[];
  isLoading: boolean;
  error: string | null;
  unsubscribe: Unsubscribe | null;
  
  // Actions
  loadEvents: (residenceId: string) => Promise<void>;
  subscribeToEvents: (residenceId: string) => void;
  unsubscribeFromEvents: () => void;
  addEvent: (event: Omit<CalendarEvent, 'id'>, residenceId: string) => Promise<string | null>;
  updateEvent: (id: string, data: Partial<CalendarEvent>, residenceId: string) => Promise<boolean>;
  deleteEvent: (id: string, residenceId: string) => Promise<boolean>;
  getEventById: (id: string) => CalendarEvent | undefined;
  clearEvents: () => void;
}

// ========================================
// HELPER: Envoyer une notification
// ========================================

const sendEventNotification = async (
  residenceId: string,
  event: Omit<CalendarEvent, 'id'>,
  eventId: string
) => {
  try {
    const notificationsRef = collection(db, `residences/${residenceId}/notifications`);
    
    // Convertir restrictedTo en format attendu par useNotifications
    let restrictedTo: string[] = [];
    if (event.restrictedTo === 'owners') {
      restrictedTo = ['owner'];
    } else if (event.restrictedTo === 'council') {
      restrictedTo = ['council'];
    }
    // Si pas de restriction, restrictedTo reste vide = tous les utilisateurs

    // Formater la date pour l'affichage
    const eventDate = new Date(event.date);
    const dateStr = eventDate.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
    const timeStr = event.time ? ` à ${event.time}` : '';

    const typeLabel = EVENT_TYPE_LABELS[event.type] || 'Événement';

    const notificationData: Record<string, any> = {
      title: `📅 ${typeLabel}`,
      message: `"${event.title}" - ${dateStr}${timeStr}`,
      type: 'info',
      createdAt: Timestamp.now(),
      sentBy: event.createdBy || 'system',
      sentByName: event.createdByName || 'Système',
      readBy: [],
      link: `/calendar` // Lien vers le calendrier
    };

    // Ajouter restrictedTo seulement si non vide
    if (restrictedTo.length > 0) {
      notificationData.restrictedTo = restrictedTo;
    }

    await addDoc(notificationsRef, notificationData);
    logger.db(' Notification sent for new event:', event.title);
  } catch (error) {
    console.error('[eventStore] Error sending notification:', error);
    // Ne pas bloquer la création de l'événement si la notification échoue
  }
};

export const useEventStore = create<EventState>((set, get) => ({
  events: [],
  isLoading: false,
  error: null,
  unsubscribe: null,

  // Charger les événements depuis Firestore
  loadEvents: async (residenceId: string) => {
    if (!residenceId) {
      set({ events: [], isLoading: false });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const eventsRef = collection(db, 'residences', residenceId, 'events');
      const q = query(eventsRef, orderBy('date', 'asc'));
      const snapshot = await getDocs(q);

      const events: CalendarEvent[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || '',
          date: data.date?.toDate?.()?.toISOString() || data.date || '',
          time: data.time || undefined,
          endTime: data.endTime || undefined,
          type: data.type || 'autre',
          description: data.description || undefined,
          location: data.location || undefined,
          restrictedTo: data.restrictedTo || undefined,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || undefined,
          createdBy: data.createdBy || undefined,
          createdByName: data.createdByName || undefined,
          residenceId: residenceId
        };
      });

      set({ events, isLoading: false });
    } catch (error) {
      console.error('Error loading events:', error);
      set({ error: 'Erreur lors du chargement des événements', isLoading: false });
    }
  },

  // S'abonner aux événements en temps réel
  subscribeToEvents: (residenceId: string) => {
    if (!residenceId) return;

    // Se désabonner d'abord si déjà abonné
    const { unsubscribe: currentUnsubscribe } = get();
    if (currentUnsubscribe) {
      currentUnsubscribe();
    }

    set({ isLoading: true });

    const eventsRef = collection(db, 'residences', residenceId, 'events');
    const q = query(eventsRef, orderBy('date', 'asc'));

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const events: CalendarEvent[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title || '',
            date: data.date?.toDate?.()?.toISOString() || data.date || '',
            time: data.time || undefined,
            endTime: data.endTime || undefined,
            type: data.type || 'autre',
            description: data.description || undefined,
            location: data.location || undefined,
            restrictedTo: data.restrictedTo || undefined,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || undefined,
            createdBy: data.createdBy || undefined,
            createdByName: data.createdByName || undefined,
            residenceId: residenceId
          };
        });

        set({ events, isLoading: false, error: null });
      },
      (error) => {
        console.error('Error subscribing to events:', error);
        set({ error: 'Erreur de connexion aux événements', isLoading: false });
      }
    );

    set({ unsubscribe });
  },

  // Se désabonner
  unsubscribeFromEvents: () => {
    const { unsubscribe } = get();
    if (unsubscribe) {
      unsubscribe();
      set({ unsubscribe: null });
    }
  },

  // Ajouter un événement
  addEvent: async (eventData: Omit<CalendarEvent, 'id'>, residenceId: string) => {
    if (!residenceId) return null;

    try {
      const eventsRef = collection(db, 'residences', residenceId, 'events');
      
      const docData: any = {
        title: eventData.title,
        date: Timestamp.fromDate(new Date(eventData.date)),
        type: eventData.type || 'autre',
        createdAt: Timestamp.now()
      };

      // Ajouter les champs optionnels seulement s'ils existent
      if (eventData.time) docData.time = eventData.time;
      if (eventData.endTime) docData.endTime = eventData.endTime;
      if (eventData.description) docData.description = eventData.description;
      if (eventData.location) docData.location = eventData.location;
      if (eventData.restrictedTo) docData.restrictedTo = eventData.restrictedTo;
      if (eventData.createdBy) docData.createdBy = eventData.createdBy;
      if (eventData.createdByName) docData.createdByName = eventData.createdByName;

      const docRef = await addDoc(eventsRef, docData);
      
      logger.db('Event added with ID:', docRef.id);

      // ✅ Envoyer une notification à l'audience concernée
      await sendEventNotification(residenceId, eventData, docRef.id);

      return docRef.id;
    } catch (error) {
      console.error('Error adding event:', error);
      return null;
    }
  },

  // Mettre à jour un événement
  updateEvent: async (id: string, data: Partial<CalendarEvent>, residenceId: string) => {
    if (!residenceId || !id) return false;

    try {
      const eventRef = doc(db, 'residences', residenceId, 'events', id);
      
      const updateData: any = {};
      
      if (data.title !== undefined) updateData.title = data.title;
      if (data.date !== undefined) updateData.date = Timestamp.fromDate(new Date(data.date));
      if (data.time !== undefined) updateData.time = data.time || null;
      if (data.endTime !== undefined) updateData.endTime = data.endTime || null;
      if (data.type !== undefined) updateData.type = data.type;
      if (data.description !== undefined) updateData.description = data.description || null;
      if (data.location !== undefined) updateData.location = data.location || null;
      if (data.restrictedTo !== undefined) updateData.restrictedTo = data.restrictedTo || null;

      await updateDoc(eventRef, updateData);
      
      logger.db('Event updated:', id);
      return true;
    } catch (error) {
      console.error('Error updating event:', error);
      return false;
    }
  },

  // Supprimer un événement
  deleteEvent: async (id: string, residenceId: string) => {
    if (!residenceId || !id) return false;

    try {
      const eventRef = doc(db, 'residences', residenceId, 'events', id);
      await deleteDoc(eventRef);
      
      logger.db('Event deleted:', id);
      return true;
    } catch (error) {
      console.error('Error deleting event:', error);
      return false;
    }
  },

  // Obtenir un événement par ID
  getEventById: (id: string) => {
    return get().events.find(event => event.id === id);
  },

  // Vider les événements
  clearEvents: () => {
    const { unsubscribe } = get();
    if (unsubscribe) {
      unsubscribe();
    }
    set({ events: [], unsubscribe: null });
  }
}));

export default useEventStore;
